import type { JSX, ReactNode } from 'react';
import type { EnforcedPlanFeature } from '@bookora/shared';

import { usePlanLock } from './plan-access';
import { PlanLockBanner } from './plan-lock-banner';
import { PlanLockedState, type PlanLockedVariant } from './plan-locked-state';

export interface RequirePlanFeatureProps {
  feature: EnforcedPlanFeature;
  variant: PlanLockedVariant;
  /**
   * Analytics with no read-only mode: a locked feature renders the lock even
   * when the clinic has records of it. No screen in the panel passes it today
   * — owner round 4 / C5 keep P&L, revenue and costs open on every plan and
   * withhold only profit LINES — it is kept for a future computed screen.
   */
  computed?: boolean;
  /** The section's name, for `variant="section"`. */
  sectionLabel?: string;
  children?: ReactNode;
}

/**
 * The one wrapper a screen, a route or a tab puts around a plan feature:
 *
 *   open       → the children, untouched
 *   locked     → `PlanLockedState` (the pitch, the plan, the upgrade path)
 *   read-only  → the banner, then the children under `PlanLockContext`
 *                (or the lock, for `computed`)
 *
 * Which feature a screen needs is `section-features.ts`, the ONE map. Fails
 * open: while plan access loads, or when it failed, the children render and
 * the server's 402 is the gate.
 */
export function RequirePlanFeature({
  feature,
  variant,
  computed = false,
  sectionLabel,
  children,
}: RequirePlanFeatureProps): JSX.Element {
  const lock = usePlanLock(feature);
  if (lock.state === 'locked' || (lock.state === 'read-only' && computed)) {
    return <PlanLockedState feature={feature} variant={variant} sectionLabel={sectionLabel} />;
  }
  // Open and read-only share one tree (the banner draws its notice or
  // nothing), so the answer arriving never remounts the screen.
  return <PlanLockBanner feature={feature}>{children}</PlanLockBanner>;
}
